import React from 'react';

import cloneDeep from 'lodash/cloneDeep';
import { useForm, useFormState } from 'react-final-form';

import Button from '@atlaskit/button/standard-button';
import DropdownMenu, {
  DropdownItem,
  DropdownItemGroup,
} from '@atlaskit/dropdown-menu';
import CopyIcon from '@atlaskit/icon/glyph/copy';
import GraphBarIcon from '@atlaskit/icon/glyph/graph-bar';
import LockFilledIcon from '@atlaskit/icon/glyph/lock-filled';
// TODO: move dropdown to a molecule
import MoreIcon from '@atlaskit/icon/glyph/more';
import TrashIcon from '@atlaskit/icon/glyph/trash';
import UnlockIcon from '@atlaskit/icon/glyph/unlock';
import Tooltip from '@atlaskit/tooltip';

import { Button as SoldeveloButton } from '@qality/common-components/src/atoms/Button';
import { Flex } from '@qality/common-components/src/atoms/Flex';
import { Table } from '@qality/common-components/src/atoms/Table';
import { Text } from '@qality/common-components/src/atoms/Text';
import { formatDate } from '@qality/common-components/src/lib/util/dateTime';
import { stopPropagation } from '@qality/common-components/src/lib/util/event';
import { Issue } from '@qality/common-components/src/molecules/Issue';
import { OpenClosedStatusComponent } from '@qality/common-components/src/molecules/OpenClosedStatusComponent';
import { FEATURE_FLAG } from '@qality/common-config';
import { TestCycle } from '@qality/common-model';
import {
  StorageOfUniqueServices,
  TestCycleHelper,
  TestCycleService,
  TestCyclesService,
} from '@qality/common-service';

import { ExecutionStatusSummary } from '../../execution-status-summary/ExecutionStatusSummary';
import {
  DropdownOptionIconStyled,
  DropdownOptionLabelStyled,
  DropdownOptionStyled,
} from './TestCyclesOverviewTable.styled';

export interface TestCyclesOverviewTableViewProps {
  testCycle: TestCycle;
  hasActionsPermissions: boolean;
  rowNumber: number;
  onDelete: (testCycle: TestCycle) => void;
}

export const TestCyclesOverviewRow: React.FC<TestCyclesOverviewTableViewProps> =
  ({
    testCycle,
    hasActionsPermissions,
    onDelete,
  }: TestCyclesOverviewTableViewProps) => {
    const { change } = useForm();
    const { values } = useFormState();

    // TODO: when clone will fail for any reason, what to do then
    const handleCloneAction = async () => {
      await TestCycleHelper.cloneTestCycle(testCycle.id);
      // clone do refresh but does not navigate to another page
      change('refreshCount', values.refreshCount + 1);
    };

    const handleCloseReopenAction = async () => {
      const updatedTestCycle = cloneDeep(testCycle);
      updatedTestCycle.isClosed = !testCycle.isClosed;
      await TestCyclesService.updateTestCycle(
        testCycle.id,
        updatedTestCycle.getRequestData()
      );
      change('refreshCount', values.refreshCount + 1);
    };

    const testCycleCreated = testCycle.created
      ? testCycle.created.split('[')[0]
      : null;

    const testCycleURL = StorageOfUniqueServices.get(
      'URLService'
    ).getTestCycleUrl(testCycle.id);
    const projectURL = StorageOfUniqueServices.get('URLService').getProjectUrl(
      testCycle.project.key
    );
    const versionURL = testCycle.version
      ? StorageOfUniqueServices.get('URLService').getVersionUrl(
          testCycle.project?.key,
          testCycle.version?.id
        )
      : '';

    const handleOpenTestCyle = () => {
      StorageOfUniqueServices.get('AP').navigator.go('site', {
        absoluteUrl: testCycleURL,
      });
    };

    const doPrimaryAction = handleOpenTestCyle.bind(this, testCycle.id);

    return (
      <Table.Tr
        isClickable
        isHoverable
        data-testid='TestCyclesOverviewTable.Row-Tr'
        onClick={doPrimaryAction}
      >
        <Table.Td>
          <Table.ContentHelper flex='inline-flex' onClick={stopPropagation}>
            <Issue
              title={testCycle.name}
              issueLink={testCycleURL}
              addPadding
              shouldOpenInCurrentTab
            />
          </Table.ContentHelper>
        </Table.Td>

        <Table.Td>
          <Table.ContentHelper flex='inline-flex' onClick={stopPropagation}>
            <Issue
              iconSrc={testCycle.project.iconUrl}
              title={testCycle.project.name}
              issueLink={projectURL}
              shouldOpenInCurrentTab
            />
          </Table.ContentHelper>
        </Table.Td>

        <Table.Td>
          <Table.ContentHelper
            flex='inline-flex'
            alignItems='center'
            onClick={stopPropagation}
          >
            {testCycle.version ? (
              <Issue title={testCycle.version.name} issueLink={versionURL} />
            ) : (
              <Text inactive>None</Text>
            )}
          </Table.ContentHelper>
        </Table.Td>

        <Table.Td onClick={stopPropagation}>
          <Table.ContentHelper alignItems='center'>
            <ExecutionStatusSummary statusSummary={testCycle.status} />
          </Table.ContentHelper>
        </Table.Td>

        {FEATURE_FLAG.isPremium && (
          <Table.Td>
            <Table.ContentHelper alignItems='center'>
              <OpenClosedStatusComponent isClosed={testCycle.isClosed} />
            </Table.ContentHelper>
          </Table.Td>
        )}

        <Table.Td>
          <Table.ContentHelper alignItems='center'>
            {testCycleCreated ? (
              <Text>{formatDate(testCycleCreated, 'longDateFormat')}</Text>
            ) : (
              <Text inactive>None</Text>
            )}
          </Table.ContentHelper>
        </Table.Td>
        <Table.Td>
          <Table.ContentHelper alignItems='center'>
            {testCycle.dueDate ? (
              <Text>{formatDate(testCycle.dueDate, 'longDateFormat')}</Text>
            ) : (
              <Text inactive>None</Text>
            )}
          </Table.ContentHelper>
        </Table.Td>
        {hasActionsPermissions && (
          <Table.Td onClick={stopPropagation} className='show-on-row-hover'>
            <Flex spaceBetween>
              {testCycle.canEdit && (
                <SoldeveloButton
                  mr
                  marginSize='small'
                  onClick={doPrimaryAction}
                >
                  View
                </SoldeveloButton>
              )}
              <DropdownMenu
                shouldFlip={false}
                placement='left-start'
                trigger={({ triggerRef, ...props }) => (
                  <Button
                    {...props}
                    iconBefore={
                      <MoreIcon
                        label='More'
                        size='small'
                        primaryColor='var(--ds-icon, var(--ds-fb-icon))'
                      />
                    }
                    ref={triggerRef}
                    appearance='default'
                  />
                )}
                testId='testCaseDropdown'
              >
                <DropdownItemGroup>
                  <DropdownItem
                    key='test-cycle-action-clone'
                    onClick={handleCloneAction}
                  >
                    <DropdownOptionStyled data-testid='clone-option'>
                      <DropdownOptionIconStyled>
                        <CopyIcon
                          label='Clone'
                          primaryColor='var(--ds-icon, var(--ds-fb-icon))'
                        />
                      </DropdownOptionIconStyled>
                      <DropdownOptionLabelStyled>
                        Clone
                      </DropdownOptionLabelStyled>
                    </DropdownOptionStyled>
                  </DropdownItem>
                  {FEATURE_FLAG.isPremium && (
                    <>
                      {testCycle.isClosed ? (
                        <DropdownItem
                          key='test-cycle-action-reopen'
                          onClick={handleCloseReopenAction}
                        >
                          <DropdownOptionStyled data-testid='reopen-option'>
                            <DropdownOptionIconStyled>
                              <UnlockIcon
                                label='Reopen'
                                primaryColor='var(--ds-icon, var(--ds-fb-icon))'
                              />
                            </DropdownOptionIconStyled>
                            <DropdownOptionLabelStyled>
                              Reopen
                            </DropdownOptionLabelStyled>
                          </DropdownOptionStyled>
                        </DropdownItem>
                      ) : (
                        <DropdownItem
                          key='test-cycle-action-close'
                          onClick={handleCloseReopenAction}
                        >
                          <DropdownOptionStyled data-testid='close-option'>
                            <DropdownOptionIconStyled>
                              <LockFilledIcon
                                label='Close'
                                primaryColor='var(--ds-icon, var(--ds-fb-icon))'
                              />
                            </DropdownOptionIconStyled>
                            <DropdownOptionLabelStyled>
                              Close
                            </DropdownOptionLabelStyled>
                          </DropdownOptionStyled>
                        </DropdownItem>
                      )}
                    </>
                  )}
                  <Tooltip
                    content={
                      !FEATURE_FLAG.isPremium
                        ? 'Test Execution Report of Test Cycle available in QAlity Plus'
                        : ''
                    }
                    position='left'
                  >
                    <DropdownItem
                      key='test-cycle-execution-report'
                      onClick={() =>
                        TestCycleService.navigateToTestExecutionReportOfCycle(
                          testCycle.id,
                          testCycle.project
                        )
                      }
                      isDisabled={!FEATURE_FLAG.isPremium}
                    >
                      <DropdownOptionStyled>
                        <DropdownOptionIconStyled>
                          <GraphBarIcon
                            label='Test Execution Report'
                            primaryColor={
                              FEATURE_FLAG.isPremium
                                ? 'var(--ds-icon, var(--ds-fb-icon))'
                                : 'var(--ds-icon-disabled, var(--ds-fb-icon-disabled))'
                            }
                          />
                        </DropdownOptionIconStyled>
                        <DropdownOptionLabelStyled>
                          Test Execution Report
                        </DropdownOptionLabelStyled>
                      </DropdownOptionStyled>
                    </DropdownItem>
                  </Tooltip>
                  <DropdownItem
                    key='test-cycle-action-delete'
                    onClick={() => {
                      onDelete(testCycle);
                    }}
                  >
                    <DropdownOptionStyled data-testid='delete-option'>
                      <DropdownOptionIconStyled>
                        <TrashIcon
                          label='Delete'
                          primaryColor='var(--ds-icon, var(--ds-fb-icon))'
                        />
                      </DropdownOptionIconStyled>
                      <DropdownOptionLabelStyled>
                        Delete
                      </DropdownOptionLabelStyled>
                    </DropdownOptionStyled>
                  </DropdownItem>
                </DropdownItemGroup>
              </DropdownMenu>
            </Flex>
          </Table.Td>
        )}
      </Table.Tr>
    );
  };
